/**
 * Provider Audit Log Page
 * Shows provider's own activity history with filtering
 */

import { authenticatedFetch } from "@lumi/data";
import { useEffect, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import {
  type AuditLog,
  type AuditLogFilterValues,
  type AuditLogResponse,
  AuditLogFilters,
  AuditLogRow,
} from "../../../shared/components/audit";
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner";
import { Button } from "../../../shared/components/ui/button";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../../../shared/components/ui/table";

/**
 * Provider Audit Log - Shows provider's own actions
 */
// eslint-disable-next-line max-lines-per-function
export function ProviderAuditLog(): ReactElement {
  const { accessToken, refreshAuth } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditLogFilterValues>({ action: "", entityType: "", userId: "" });
  const [searchParams, setSearchParams] = useSearchParams();

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Get current page from URL
  const currentPage = Number.parseInt(searchParams.get("page") || "1", 10);

  /**
   * Fetch audit logs from API
   */
  const fetchAuditLogs = async () => {
    if (!accessToken) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("limit", "20");

      // Apply filters from URL
      const actionFilter = searchParams.get("action");
      const entityTypeFilter = searchParams.get("entityType");

      if (actionFilter) {
        params.append("action", actionFilter);
      }
      if (entityTypeFilter) {
        params.append("entityType", entityTypeFilter);
      }

      const result = await authenticatedFetch<AuditLogResponse>(
        `${API_BASE}/v1/provider/audit-log?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setLogs(result.data);
      setPagination({
        page: result.pagination.page,
        limit: result.pagination.limit,
        total: result.pagination.total,
        totalPages: result.pagination.pages,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update filter state when URL changes
   */
  useEffect(() => {
    setFilters({
      action: searchParams.get("action") || "",
      entityType: searchParams.get("entityType") || "",
      userId: "", // Not used for provider view
    });
    void fetchAuditLogs();
  }, [searchParams, accessToken]);

  /**
   * Handle filter changes - auto-apply to URL
   */
  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Auto-apply to URL
    const newParams = new URLSearchParams();
    newParams.set("page", "1"); // Reset to page 1 when filtering

    if (newFilters.action) {
      newParams.set("action", newFilters.action);
    }
    if (newFilters.entityType) {
      newParams.set("entityType", newFilters.entityType);
    }

    setSearchParams(newParams);
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({ action: "", entityType: "", userId: "" });
    setSearchParams(new URLSearchParams({ page: "1" }));
  };

  /**
   * Navigate to a different page
   */
  const goToPage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  return (
    <div className="space-y-8 py-8">
      {/* Page Header */}
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Activity Log</h1>
          <p className="text-sm text-neutral-400 pb-6">View your recent actions and submission history</p>
        </div>

        {/* Filter Bar - Provider mode (no user filter, provider-specific actions) */}
        <AuditLogFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
          hideUserFilter
          providerMode
        />
      </div>

      {/* Content */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {error && <div className="text-center py-8 text-destructive-foreground">Error: {error}</div>}

      {!loading && !error && logs.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">No activity logs found</div>
      )}

      {!loading && !error && logs.length > 0 && (
        <>
          <div className="border border-white/0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, index) => (
                  <AuditLogRow key={log.id} log={log} index={index} hideUser />
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
